import React, { useState } from "react";
import { TrendingUp, Phone, DollarSign, Clock, ExternalLink } from "lucide-react";
import { PLAN_DATA } from "./demoConfig";

const OBJECTION_META = {
  price: { label: "Price", bg: "#FAECE7", color: "#712B13" },
  timing: { label: "Timing", bg: "#FAEEDA", color: "#633806" },
  trust: { label: "Trust", bg: "#E6F1FB", color: "#0C447C" },
  diy: { label: "DIY", bg: "#EEEDFE", color: "#3C3489" },
  competitor: { label: "Competitor", bg: "#F1EFE8", color: "#444441" },
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function CloserPerformance({ leads, auth }) {
  const [revWindow, setRevWindow] = useState("week"); // "week" | "month"
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const myLeads = leads.filter((l) => l.closerUsername === auth.username);
  const myRecent = myLeads.filter((l) => l.demoDoneAt && new Date(l.demoDoneAt) >= thirtyDaysAgo);

  // Stats
  const demosRun = myRecent.length;
  const myConverted = myRecent.filter((l) => l.status === "converted");
  const closeRate = demosRun > 0 ? Math.round((myConverted.length / demosRun) * 100) : 0;

  // Avg revenue toggle: week or month window using paidAt / convertedAt
  const windowDays = revWindow === "week" ? 7 : 30;
  const windowStart = new Date(now.getTime() - windowDays * 24 * 60 * 60 * 1000);
  const myConvInWindow = myLeads.filter((l) => {
    const dt = l.paidAt || l.convertedAt;
    return l.status === "converted" && dt && new Date(dt) >= windowStart;
  });
  const totalRevWindow = myConvInWindow.reduce((s, l) => s + (Number(l.paidAmount) || Number(l.convertedAmount) || 0), 0);
  const avgRevenue = myConvInWindow.length > 0 ? Math.round(totalRevWindow / myConvInWindow.length) : 0;

  const durations = myRecent.map((l) => l.callDurationMinutes).filter(Boolean);
  const avgDuration = durations.length > 0
    ? (durations.reduce((a, b) => a + b, 0) / durations.length).toFixed(1)
    : null;

  // Objection breakdown
  const objCounts = {};
  myRecent.forEach((l) => {
    (l.objectionTags || []).forEach((tag) => {
      objCounts[tag] = (objCounts[tag] || 0) + 1;
    });
  });
  const maxObj = Math.max(...Object.values(objCounts), 1);

  // Recent calls (last 10)
  const recentCalls = [...myRecent]
    .sort((a, b) => new Date(b.demoDoneAt) - new Date(a.demoDoneAt))
    .slice(0, 10);

  return (
    <div className="closer-perf" data-testid="closer-performance">
      <h3 className="closer-perf-title">My performance — last 30 days</h3>

      {/* Row 1: Stat cards */}
      <div className="cp-stats-row" data-testid="cp-stats">
        <div className="cp-stat-card">
          <Phone className="w-4 h-4" />
          <span className="cp-stat-value">{demosRun}</span>
          <span className="cp-stat-label">Demos run</span>
        </div>
        <div className="cp-stat-card">
          <TrendingUp className="w-4 h-4" />
          <span className="cp-stat-value">{closeRate}%</span>
          <span className="cp-stat-label">Close rate</span>
        </div>
        <div className="cp-stat-card" data-testid="cp-avg-revenue-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <DollarSign className="w-4 h-4" />
            <div role="tablist" style={{ display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 999, overflow: "hidden", fontSize: 10 }}>
              <button
                role="tab"
                onClick={() => setRevWindow("week")}
                data-testid="cp-rev-toggle-week"
                style={{
                  padding: "3px 8px",
                  background: revWindow === "week" ? "#7F77DD" : "transparent",
                  color: revWindow === "week" ? "#fff" : "#6b7280",
                  border: "none", cursor: "pointer", fontWeight: 600,
                }}
              >Week</button>
              <button
                role="tab"
                onClick={() => setRevWindow("month")}
                data-testid="cp-rev-toggle-month"
                style={{
                  padding: "3px 8px",
                  background: revWindow === "month" ? "#7F77DD" : "transparent",
                  color: revWindow === "month" ? "#fff" : "#6b7280",
                  border: "none", cursor: "pointer", fontWeight: 600,
                }}
              >Month</button>
            </div>
          </div>
          <span className="cp-stat-value" data-testid="cp-avg-revenue-value">${avgRevenue}</span>
          <span className="cp-stat-label">Avg revenue ({revWindow === "week" ? "this week" : "this month"})</span>
        </div>
        <div className="cp-stat-card">
          <Clock className="w-4 h-4" />
          <span className="cp-stat-value">{avgDuration || "—"} min</span>
          <span className="cp-stat-label">Avg call duration</span>
        </div>
      </div>

      {/* Row 2: Objection bar chart */}
      {Object.keys(objCounts).length > 0 && (
        <div className="cp-obj-section" data-testid="cp-objections">
          <span className="cp-section-label">Objection breakdown</span>
          <div className="cp-obj-bars">
            {Object.entries(OBJECTION_META).map(([key, meta]) => {
              const count = objCounts[key] || 0;
              const pct = maxObj > 0 ? Math.round((count / maxObj) * 100) : 0;
              return (
                <div key={key} className="cp-obj-row">
                  <span className="cp-obj-label">{meta.label}</span>
                  <div className="cp-obj-track">
                    <div className="cp-obj-fill" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                  <span className="cp-obj-count">{count > 0 ? `${Math.round((count / myRecent.length) * 100)}%` : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 3: Recent calls list */}
      {recentCalls.length > 0 && (
        <div className="cp-recent" data-testid="cp-recent-calls">
          <span className="cp-section-label">Recent calls</span>
          <div className="cp-recent-list">
            {recentCalls.map((l) => (
              <div key={l.id} className="cp-recent-row">
                <span className="cp-recent-name">{l.clientName} {l.clientLastName}</span>
                <span className="cp-recent-role">{l.targetRole}</span>
                <span className="cp-recent-dur">{l.callDurationMinutes ? `${l.callDurationMinutes}m` : "—"}</span>
                <span className="cp-recent-tags">
                  {(l.objectionTags || []).map((tag) => {
                    const m = OBJECTION_META[tag];
                    return m ? <span key={tag} className="cp-recent-tag" style={{ background: m.bg, color: m.color }}>{m.label}</span> : null;
                  })}
                </span>
                <span className={`cp-recent-status cp-status-${l.status}`}>
                  {l.status === "converted" ? "Converted" : l.status === "cold" ? "Cold" : l.status === "payment_sent" ? "Payment sent" : "Follow up"}
                </span>
                <span className="cp-recent-date">{formatDate(l.demoDoneAt)}</span>
                {l.fathomRecordingUrl && (
                  <a href={l.fathomRecordingUrl} target="_blank" rel="noopener noreferrer" className="cp-recent-link">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {demosRun === 0 && (
        <p className="cp-empty">No demos in the last 30 days. Start a demo to see your stats here.</p>
      )}
    </div>
  );
}
